import "./application";
import '../utils/helpers/datetimeLocalization';
import '../utils/bugsnag';
import '../apps/on_boarding/main.ts';
import '../apps/plan_trail/main';
import '../apps/comments/main';
import '../apps/download_modal/main';
import '../apps/flyover_preview/main.ts';
import '../apps/flyover_share/main.ts';
import '../apps/embed_builder/main';
import '../apps/edit_hike_details/main.ts';
import '../apps/itinerary_overview/main';
import { OnBoardingGoal } from '../utils/models/OnBoardingGoal';
import { RevenueCatWrapper } from '../utils/RevenueCatWrapper';
import graph_client from '../utils/graphql/graph_client';
import { openFlyoverVideoFullscreen } from '../utils/openFlyoverVideoFullscreen';
import { shareContextFromDataset } from '../apps/flyover_share/readyVideoShare';
import { DestroyHike, SubmitHikeForOfficial, UpdateHikeWithPlannedTrails } from '../utils/graphql/hikes-graph';
import { openHikeTrailerShare } from '../apps/flyover_share/openHikeTrailerShare';
import { hikeFromDataset } from '../apps/flyover_share/trailerShare';
import { i18n } from '../i18n';
import { isPlannerMobile } from '../apps/plan_trail/plannerMobileGate';

const t = i18n.global.t;

declare global {
  interface Window {
    printingUrl?: string;
    openEditHikeDetails?: () => void;
    openItineraryModal?: () => void;
  }
}

// Trail Trailer thumb in the hero (same markup + overlay as the recording page's flyover thumb).
// Its data-* attributes carry the share context too, so the player offers Share (`hike_overview`).
document.querySelectorAll<HTMLElement>('.play-flyover-video').forEach((el) => {
  el.addEventListener('click', (e) => {
    e.preventDefault();
    const ds = el.dataset;
    if (ds.videoUrl) openFlyoverVideoFullscreen(ds.videoUrl, shareContextFromDataset(ds));
  });
});

// The AI Planner section links into the planner, which phones and tablets
// don't get (they're offered the app instead) — so they aren't offered it.
// Narrow screens never see it (app.scss); this catches the rest.
if (isPlannerMobile()) {
  document.querySelectorAll('.ai-planner-section').forEach((section) => section.remove());
}

// "Hike trailer" in the More menu: share the hike's video. Its data-* attributes carry the hike.
document.querySelectorAll<HTMLElement>('.share-hike-trailer').forEach((el) => {
  el.addEventListener('click', () => {
    void openHikeTrailerShare(hikeFromDataset(el.dataset), 'hike_overview');
  });
});

const gpxDownloadButtons = document.querySelectorAll('.gpx-download');
const addToTrailListButton = document.querySelector('.add-to-trail-list');
const completeHikeOnboardingButtons = document.querySelectorAll('.complete-hike-onboarding');
const dropdownItems = document.querySelectorAll('.dropdown-item');
const copyToHikesLoginButtons = document.querySelectorAll('.copy-to-hikes-login');

const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content;
const printMapDownloadButtons = document.querySelectorAll('.print-map-download');

if (gpxDownloadButtons) {
  gpxDownloadButtons.forEach((gpxDownloadButton) => {
    gpxDownloadButton.addEventListener('click', () => {
      window.openDownloadModal();
    });
  });
}

if (addToTrailListButton) {
  addToTrailListButton.addEventListener('click', () => {
    window.onBoardingVue.launchFor(OnBoardingGoal.AddTrailToList);
  });
}

if (completeHikeOnboardingButtons) {
  completeHikeOnboardingButtons.forEach((button) => {
    button.addEventListener('click', () => {
      window.onBoardingVue?.launchFor(OnBoardingGoal.CompleteHike);
    });
  });
}

if (dropdownItems) {
  dropdownItems.forEach((dropdownItem) => {
    // The menu is server-rendered in the visitor's language, so match each item
    // against the same locale key the ERB used rather than the English label.
    const itemLabel = dropdownItem.textContent?.trim();
    if (itemLabel === t('hikes.show.send_to_garmin')) {
      dropdownItem.addEventListener('click', () => {
        if (window.openDownloadModal) {
          window.openDownloadModal('gps');
        }
      });
    }
    if (itemLabel === t('hikes.show.share_hike')) {
      dropdownItem.addEventListener('click', async () => {
        const url = window.location.href;
        try {
          await navigator.clipboard.writeText(url);
          alert(t('entrypoints.hikes.link_copied_to_clipboard'));
        } catch {
          // Clipboard API unavailable (e.g. non-secure context) — fall back to a prompt.
          window.prompt(t('entrypoints.hikes.copy_this_link_to_share'), url);
        }
      });
    }
    if (itemLabel === t('hikes.show.report_an_issue')) {
      dropdownItem.addEventListener('click', async () => {
        const obfuscatedSlug = window.obfuscated_slug;
        const slug = window.slug;
        const regionSlug = window.region_slug;
        const countrySlug = window.country_slug;

        let reportUrl: string;
        if (countrySlug && regionSlug && slug) {
          reportUrl = `/hikes/${countrySlug}/${regionSlug}/${slug}/report_issue`;
        } else if (obfuscatedSlug) {
          reportUrl = `/hikes/users/${obfuscatedSlug}/report_issue`;
        } else {
          alert(t('entrypoints.hikes.unable_to_report_issue'));
          return;
        }

        try {
          const response = await fetch(reportUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || '',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            alert(t('entrypoints.hikes.thank_you_for_reporting'));
          } else {
            alert(t('entrypoints.hikes.unable_to_report_issue_try_later'));
          }
        } catch (error) {
          console.error('Error reporting issue:', error);
          alert(t('entrypoints.hikes.unable_to_report_issue_try_later'));
        }
      });
    }
  });
}

if (copyToHikesLoginButtons) {
  copyToHikesLoginButtons.forEach((button) => {
    const triggerLoginFlow = () => {
      const onboarding = window.onBoardingVue;
      if (onboarding && typeof onboarding.launchFor === 'function') {
        onboarding.launchFor(OnBoardingGoal.CreateAccount);
        return;
      }

      window.location.href = '/login';
    };

    button.addEventListener('click', triggerLoginFlow);
    button.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        triggerLoginFlow();
      }
    });
  });
}

if (printMapDownloadButtons) {
  printMapDownloadButtons.forEach((printMapDownloadButton) => {
    printMapDownloadButton.addEventListener('click', async () => {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      if (isProOrProPlus) {
        if (window.printingUrl) {
          window.location.href = window.printingUrl;
        } else {
          console.warn('No print map URL is available for this hike.');
        }
      } else {
        window.location.href = '/purchases';
      }
    });
  });
}

// Delete hike handler
const deleteHikeButton = document.getElementById('delete-hike');
if (deleteHikeButton) {
  deleteHikeButton.addEventListener('click', async () => {
    const trailName = window.trailName || t('entrypoints.hikes.this_hike');
    if (!window.confirm(t('entrypoints.hikes.confirm_delete_hike', { name: trailName }))) {
      return;
    }

    const hikeId = window.hike_id || window.id;
    if (!hikeId) {
      alert(t('entrypoints.hikes.unable_to_delete_hike_no_id'));
      return;
    }

    try {
      await graph_client.request(DestroyHike, { input: { id: hikeId } });
      // Redirect to user's hikes page after successful deletion
      window.location.href = '/user/hikes';
    } catch (error: unknown) {
      console.error('Error deleting hike:', error);
      let errorMessage = t('entrypoints.hikes.unknown_error');
      if (error && typeof error === 'object') {
        const err = error as { response?: { errors?: Array<{ message?: string }> }; message?: string };
        errorMessage = err.response?.errors?.[0]?.message || err.message || errorMessage;
      }
      alert(t('entrypoints.hikes.failed_to_delete_hike', { error: errorMessage }));
    }
  });
}

// Submit hike to be made official handler
const submitOfficialButton = document.getElementById('submit-official-hike');
if (submitOfficialButton) {
  submitOfficialButton.addEventListener('click', async () => {
    if (!window.confirm(t('entrypoints.hikes.confirm_submit_official'))) {
      return;
    }

    const hikeId = window.hike_id || window.id;
    if (!hikeId) {
      alert(t('entrypoints.hikes.unable_to_submit_hike_no_id'));
      return;
    }

    try {
      const data = await graph_client.request(SubmitHikeForOfficial, { input: { id: hikeId } });
      const errors = data?.submitHikeForOfficial?.errors;
      if (errors && errors.length) {
        alert(t('entrypoints.hikes.failed_to_submit_hike', { error: errors.map((e: { errors: string[] }) => e.errors.join(', ')).join('; ') }));
        return;
      }
      // Swap the action in place for the disabled "pending" state (mirrors the ERB),
      // so no full page reload is needed.
      const pending = document.createElement('span');
      pending.className = 'dropdown-item disabled text-muted';
      pending.textContent = t('hikes.show.official_request_pending');
      submitOfficialButton.replaceWith(pending);
      alert(t('entrypoints.hikes.hike_submitted_for_review'));
    } catch (error: unknown) {
      console.error('Error submitting hike for official review:', error);
      let errorMessage = t('entrypoints.hikes.unknown_error');
      if (error && typeof error === 'object') {
        const err = error as { response?: { errors?: Array<{ message?: string }> }; message?: string };
        errorMessage = err.response?.errors?.[0]?.message || err.message || errorMessage;
      }
      alert(t('entrypoints.hikes.failed_to_submit_hike', { error: errorMessage }));
    }
  });
}

// Edit Details handler — opens the Vue modal mounted at #edit-hike-details (owner-gated in the view)
const editDetailsButton = document.getElementById('edit-hike-details-btn');
if (editDetailsButton) {
  editDetailsButton.addEventListener('click', () => {
    if (typeof window.openEditHikeDetails === 'function') {
      window.openEditHikeDetails();
    }
  });
}

// Itinerary handler — opens the Vue modal mounted at #itinerary-overview-app
const itineraryMenuItem = document.getElementById('itinerary-menu-item');
if (itineraryMenuItem) {
  itineraryMenuItem.addEventListener('click', () => {
    if (typeof window.openItineraryModal === 'function') {
      window.openItineraryModal();
    }
  });
}

// Remove official status handler (trails-team users only; gated server-side in the view)
const removeOfficialButton = document.getElementById('remove-official-hike');
if (removeOfficialButton) {
  removeOfficialButton.addEventListener('click', async () => {
    if (!window.confirm(t('entrypoints.hikes.confirm_remove_official'))) {
      return;
    }

    const hikeId = window.hike_id || window.id;
    if (!hikeId) {
      alert(t('entrypoints.hikes.unable_to_update_hike_no_id'));
      return;
    }

    try {
      const data = await graph_client.request(UpdateHikeWithPlannedTrails, { input: { id: hikeId, removeOfficial: true } });
      const errors = data?.updateHike?.errors;
      if (errors && errors.length) {
        alert(t('entrypoints.hikes.failed_to_remove_official', { error: errors.map((e: { errors: string[] }) => e.errors.join(', ')).join('; ') }));
        return;
      }
      window.location.reload();
    } catch (error: unknown) {
      console.error('Error removing official status:', error);
      let errorMessage = t('entrypoints.hikes.unknown_error');
      if (error && typeof error === 'object') {
        const err = error as { response?: { errors?: Array<{ message?: string }> }; message?: string };
        errorMessage = err.response?.errors?.[0]?.message || err.message || errorMessage;
      }
      alert(t('entrypoints.hikes.failed_to_remove_official', { error: errorMessage }));
    }
  });
}
